import { Spinner } from "components/spinner/spinner";
import { useEffect, useRef, useState } from "react";
import { ReactBarcode } from "react-jsbarcode";
import { connect, useDispatch } from "react-redux";
import { DeleteIcon } from "static/images/svgs/delete-icon-svg";
import { AppDispatch, ApplicationState } from "store/application-state";
import {
  activateteCard,
  getCardDetailsByBarcode,
  redeemCard,
  setActionLoading,
  setErrors,
  setInitialState,
  setLoading,
  setProductOperationsProps,
  validateCard,
} from "store/slices/product-operations";
import { isEmptyOrSpaces } from "utils/data-type-check";
import { formatNumber } from "utils/numbers";
import {
  getProductType,
  getProductUsageType,
  getStatus,
} from "utils/product-item.util";
import translate from "utils/translations";
import { CardOperationProps } from "./card-operation.props";
import { getDate, statusClasses } from "./card-operation.utils";
import { RechargeBox } from "./recharge-component.container";
import { RedeemBox } from "./redeem-component.container";

export const CardOperations = (props: CardOperationProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showRedeem, setShowRedeem] = useState<boolean>(false);
  const [showRecharge, setShowRecharge] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<null | {
    action: "redeem" | "activate";
    payload?: any;
  }>(null);
  const primaryDisabled = !props.cardDetails || props.cardDetails.status === 3;
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    dispatch(setInitialState());
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    const delay = setTimeout(() => {
      if (/^.{20,}$/.test(props.cardInputValue!)) {
        props.getCardDetails();
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [props.cardInputValue]);

  const validateRedemption = () => {
    if (!props.cardDetails) return;
    const amount = Number(props.redemptionAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      props.setError(
        translate(
          "Invalid_Redemption_Amount_Text",
          "Invalid Redemption Amount",
        ),
      );
      return;
    }
    if (amount > props.cardDetails.balance) {
      props.setError(
        translate(
          "Amount_Exceeds_Available_Balance_Text",
          "Amount exceeds available balance.",
        ),
      );
      return;
    }
    setShowConfirm({ action: "redeem", payload: { amount } });
  };
  const onPropsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    props.setProductOperationsProps({
      key: name,
      value: value,
    });
  };
  const cancelRedemption = () => {
    props.setProductOperationsProps({
      key: "redemptionAmount",
      value: "",
    });
    setShowRedeem(false);
  };
  const runAction = () => {
    if (!showConfirm) return;
    switch (showConfirm.action) {
      case "redeem":
        props.redeemCard();
        break;
      case "activate":
        props.activateCard();
        break;
    }
    setShowConfirm(null);
  };
  return (
    <div className="w-full lg:w-3/4 md:w-full min-h-dvh antialiased">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-6">
        {translate("Card_Operations_Text", "Card operations TODO")}
      </h1>

      <section className="rounded-2xl bg-white shadow-sm p-4 sm:p-6 h-3/4">
        <form
          className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center"
          role="search"
          aria-label="Card lookup"
        >
          <div className="relative flex-1">
            <label htmlFor="cardCode" className="sr-only">
              {translate("Card_Code_Text", "Card Code TODO")}
            </label>
            <input
              id="cardCode"
              ref={inputRef}
              value={props.cardInputValue}
              name="cardInputValue"
              onChange={onPropsChange}
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={props.loading}
              placeholder={translate("Enter_Code_Text", "Enter Code TODO")}
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pr-12 text-base shadow-inner focus:outline-none focus:ring-1 focus:ring-gray-300"
              aria-invalid={!!props.error}
              aria-describedby={props.error ? "code-error" : undefined}
            />
            <div className="absolute inset-y-0 right-3 flex items-center text-slate-400">
              {props.cardInputValue && (
                <button
                  onClick={() =>
                    props.setProductOperationsProps({
                      key: "cardInputValue",
                      value: "",
                    })
                  }
                  className="pointer-events-auto"
                >
                  <DeleteIcon />
                </button>
              )}
            </div>
          </div>
        </form>
        {props.error && (
          <div
            id="code-error"
            role="alert"
            className="mt-3 rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-200 px-4 py-3"
          >
            {props.error}
          </div>
        )}
        {props.cardDetails && !isEmptyOrSpaces(props.cardInputValue ?? "") && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-2xl bg-white p-5">
              <div>
                <h2 className="text-lg font-semibold mb-4">
                  {translate("Details_Text", "Details TODO")}
                </h2>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate("Product_Name_Text", "Product Name")}
                    </dt>
                    <dd className="font-mono text-sm sm:text-base break-all">
                      {props.cardDetails.productName}
                    </dd>
                  </div>
                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate("Status_Text", "Status")}
                    </dt>
                    <dd>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium ${
                          statusClasses[props.cardDetails.status] ??
                          statusClasses["1"]
                        }`}
                        aria-label={`Status ${props.cardDetails.status}`}
                      >
                        {getStatus(props.cardDetails.status)}
                      </span>
                    </dd>
                  </div>

                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate("Product_Type_Text", "Product Type")}
                    </dt>
                    <dd className="font-mono text-sm sm:text-base break-all">
                      {getProductType(props.cardDetails.productType)}
                    </dd>
                  </div>

                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate(
                        "Product_Usage_Type_Text",
                        "Product Usage Type",
                      )}
                    </dt>
                    <dd className="font-medium">
                      {getProductUsageType(props.cardDetails.usageType)}
                    </dd>
                  </div>

                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate(
                        "Remaining_Balance_Text",
                        "Remaining Balance TD",
                      )}
                    </dt>
                    <dd className="text-xl font-semibold">
                      {translate("EGP_Text", "EGP")}{" "}
                      {formatNumber(props.cardDetails.balance)}
                    </dd>
                  </div>

                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate("Activation_Date_Text", "Activation Date")}
                    </dt>
                    <dd className="text-lg font-medium">
                      {getDate(props.cardDetails?.activatedAt)}
                    </dd>
                  </div>

                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate("Expiration_Date_Text", "Expires At")}
                    </dt>
                    <dd className="text-lg font-medium">
                      {getDate(props.cardDetails?.expirationDate)}
                    </dd>
                  </div>
                  <div className="flex items-center gap-3">
                    <dt className="w-32 text-slate-500">
                      {translate("Product_Code_Text", "Product Code")}
                    </dt>
                    <dd className="text-lg font-medium">
                      {props.cardDetails?.ean13Barcode}
                    </dd>
                  </div>
                </dl>

                <div className="flex w-full items-center justify-center mt-10">
                  {props.cardDetails?.status === 1 && (
                    <ReactBarcode value={props.cardDetails.ean13Barcode} />
                  )}
                </div>
              </div>
            </div>

            <aside className="flex-col items-center justify-between">
              <div className="rounded-2xl ring-1 ring-slate-200 bg-white p-5">
                <h2 className="text-lg font-semibold mb-4">
                  {translate("Actions_Text", "Actions")}
                </h2>

                <div className="flex flex-col gap-3" aria-label="Actions">
                  {/* <button
                  type="button"
                  className="h-11 rounded-xl bg-[#518c7d] text-white font-medium shadow-sm hover:bg-[#518c7d] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#518c7d] disabled:opacity-50"
                  onClick={() => props.validateCard()}
                  disabled={!props.cardDetails || props.actionLoading !== null}
                  aria-busy={props.actionLoading === "validate"}
                >
                  {props.actionLoading === "validate" ? (
                    <Spinner label="Validating" />
                  ) : (
                    translate("Validate_Text", "Validate")
                  )}
                </button> */}

                  <button
                    type="button"
                    className="h-11 rounded-xl bg-[#518c7d] text-white font-medium shadow hover:bg-[#518c7d] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#518c7d] disabled:opacity-50"
                    onClick={() => setShowConfirm({ action: "activate" })}
                    disabled={
                      !props.cardDetails ||
                      props.cardDetails.status !== 1 ||
                      props.actionLoading !== null
                    }
                    aria-busy={props.actionLoading === "activate"}
                  >
                    {props.actionLoading === "activate" ? (
                      <Spinner label="Activating" />
                    ) : (
                      translate("Activate_Text", "Activate")
                    )}
                  </button>
                  <button
                    type="button"
                    className="h-11 rounded-xl bg-[#518c7d] text-white font-medium shadow hover:bg-[#518c7d] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#518c7d] disabled:opacity-50"
                    onClick={() => {
                      setShowRedeem(true);
                      setShowRecharge(false);
                    }}
                    disabled={
                      !props.cardDetails ||
                      props.cardDetails.status !== 2 ||
                      props.actionLoading !== null
                    }
                    aria-busy={props.actionLoading === "redeem"}
                  >
                    {props.actionLoading === "redeem" ? (
                      <Spinner label="Redeem" />
                    ) : (
                      translate("Redeem_Text", "Redeem")
                    )}
                  </button>
                  {/* <button
                  type="button"
                  className="h-11 rounded-xl bg-[#518c7d] text-white font-medium shadow hover:bg-[#518c7d] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#518c7d] disabled:opacity-50"
                  onClick={() => {
                    setShowRecharge(true);
                    setShowRedeem(false);
                  }}
                  disabled={!props.cardDetails || props.actionLoading !== null}
                  aria-busy={props.actionLoading === "recharge"}
                >
                  {props.actionLoading === "recharge" ? (
                    <Spinner label="Recharge" />
                  ) : (
                    translate("Recharge_Text", "Charge")
                  )}
                </button> */}
                </div>
              </div>
              {showRedeem && props.cardDetails ? (
                <RedeemBox
                  validateRedemption={validateRedemption}
                  cancelRedemption={cancelRedemption}
                  disabled={primaryDisabled || props.actionLoading !== null}
                  balance={(props.cardDetails?.balance ?? 0).toString()}
                />
              ) : (
                <div />
              )}
              {showRecharge && props.cardDetails ? (
                <RechargeBox
                  validateRecharge={validateRedemption}
                  disabled={primaryDisabled || props.actionLoading !== null}
                  balance={(props.cardDetails?.balance ?? 0).toString()}
                />
              ) : (
                <div />
              )}
            </aside>
          </div>
        )}
      </section>
      {/* Confirmation Modal */}
      {showConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center p-4"
        >
          <div
            className="absolute inset-0 bg-slate-900/50"
            aria-hidden
            onClick={() => setShowConfirm(null)}
          />

          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-200">
            <h3 className="text-lg font-semibold mb-2">
              {translate("Confirm_Action_Text", "Confirm Action")}
            </h3>
            <p className="text-slate-600">
              {showConfirm.action === "redeem" && (
                <>
                  {translate(
                    "Redeem_Card_Text",
                    "You're about to redeem this card with",
                  )}{" "}
                  <strong>
                    {translate("EGP_Text", "EGP")}{" "}
                    {showConfirm.payload?.amount?.toLocaleString?.()}
                  </strong>{" "}
                  {translate("From_Card_Text", "from card")}{" "}
                  <code className="font-mono">
                    {props.cardDetails?.ean13Barcode}
                  </code>
                  .
                </>
              )}
              {showConfirm.action === "activate" && (
                <>
                  {translate("Activate_Card_Text", "Activate Card")}{" "}
                  <code className="font-mono">
                    {props.cardDetails?.ean13Barcode}
                  </code>
                </>
              )}
            </p>

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                className="h-10 rounded-xl px-4 font-medium ring-1 ring-slate-300 hover:bg-slate-50"
                onClick={() => setShowConfirm(null)}
              >
                {translate("Cancel_Text", "Cancel")}
              </button>
              <button
                className="h-10 rounded-xl bg-emerald-600 px-4 font-semibold text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-200"
                onClick={runAction}
              >
                {translate("Confirm_Text", "Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const mapStateToProps = (state: ApplicationState) => ({
  cardDetails: state.productoperations.cardDetails,
  loading: state.productoperations.loading,
  actionLoading: state.productoperations.actionLoading,
  error: state.productoperations.error,
  cardInputValue: state.productoperations.cardInputValue,
  redemptionAmount: state.productoperations.redemptionAmount,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getCardDetails: () => {
    dispatch(getCardDetailsByBarcode());
  },
  setProductOperationsProps: (value: { key: string; value?: any }) => {
    dispatch(setProductOperationsProps(value));
  },
  setError: (error: string) => {
    dispatch(setErrors(error));
  },
  setActionLoading: (loading: "validate" | "activate" | "redeem" | null) => {
    dispatch(setActionLoading(loading));
  },
  setLoading: (loading: boolean) => {
    dispatch(setLoading(loading));
  },
  activateCard: () => {
    dispatch(activateteCard());
  },
  validateCard: () => {
    dispatch(validateCard());
  },
  redeemCard: () => {
    dispatch(redeemCard());
  },
});

const CardOperationsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CardOperations);
export { CardOperationsContainer };
